"use client";
// components/HoverableMention.tsx
import React, { useState, useRef, useEffect, ReactNode } from "react";
import Link from "next/link";
import { createPortal } from "react-dom";
import Image from "next/image";
import defaultProfile from "@/images/default-profile.jpg";

type Props = {
  user: {
    id: string;
    name: string;
    avatar?: string;
    bio?: string;
    display_name?: string;
    profile_picture?: {
      url: string;
      thumbnail_url: string;
    };
  };
  children: ReactNode;
  variant?: "postDisplay" | "clips" | "whoToFollow";
};

export const HoverableMention: React.FC<Props> = ({
  user,
  children,
  variant,
}) => {
  const [showCard, setShowCard] = useState(false);
  const [cardPos, setCardPos] = useState({ top: 0, left: 0 });
  const mentionRef = useRef<HTMLSpanElement>(null);
  let timeout: NodeJS.Timeout;

  const handleMouseEnter = () => {
    timeout = setTimeout(() => {
      if (mentionRef.current) {
        const rect = mentionRef.current.getBoundingClientRect();
        if (variant === "postDisplay") {
          setCardPos({
            top: rect.bottom + window.scrollY,
            left: rect.left + window.scrollX,
          });
        } else if (variant === "clips") {
          setCardPos({
            top: rect.top + window.scrollY - 70,
            left: rect.left + window.scrollX,
          });
        } else {
          setCardPos({
            top: rect.top + window.scrollY - 70,
            left: rect.left + window.scrollX - 60,
          });
        }
        setShowCard(true);
      }
    }, 200);
  };

  const handleMouseLeave = () => {
    clearTimeout(timeout);
    setShowCard(false);
  };

  useEffect(() => {
    return () => clearTimeout(timeout);
  }, []);

  const hoverCard = (
    <div
      className="absolute  z-[9999] bg-white border border-gray-200 rounded-lg shadow-lg w-fit p-3 animate-fadeIn"
      style={{ top: cardPos.top, left: cardPos.left }}
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <div className="flex items-center gap-3">
        <Link
          href={`/profile/${user.name}`}
          className="flex  items-center gap-2"
        >
          <Image
            src={
              user?.avatar ||
              user?.profile_picture?.thumbnail_url ||
              user?.profile_picture?.url ||
              defaultProfile
            }
            alt={user?.name}
            width={40}
            height={40}
            className="w-12 h-12 rounded-full object-cover border"
          />
          <div>
            <p className="font-semibold text-sm text-gray-800 ">
              {user?.display_name || user.name}
            </p>
            <p className="font-semibold text-sm text-gray-500">{user?.name}</p>
            <p className="text-xs text-gray-500 mt-1">{user.bio}</p>
          </div>
        </Link>
      </div>
    </div>
  );

  return (
    <span
      ref={mentionRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className="relative"
    >
      {children}
      {showCard && typeof window !== "undefined"
        ? createPortal(hoverCard, document.body)
        : null}
    </span>
  );
};
